/* eslint-disable react/no-multi-comp */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { omit, isEqual } from 'lodash';
import { linkNavigate } from 'src/store/location/location';
import isNewTabEvent from 'src/modules/is-new-tab-event';
import { provideQualifiedLinkParams } from 'src/modules/qualified-location';
import { provideEnrichedLocation } from 'src/modules/enriched-location';
import { getDefaultLinkTargetFromLocation } from 'src/modules/navlink-rules';
import { getVisualPropsTree } from './visual-props-tree';

const isRightClickPressed = ({ nativeEvent }) =>
  nativeEvent.type === 'contextmenu' || nativeEvent.which === 3;

class NavLink extends Component<NavLinkProps> {
  static defaultProps = {
    analytics: '',
    component: 'a',
    preNavigationHooks: [],
    allowPropagation: false
  };

  constructor(props) {
    super(props);
    this.ensureUpdatedHrefForContextMenu = this.ensureUpdatedHrefForContextMenu.bind(
      this
    );
    this.getTarget = this.getTarget.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  // Need to track mount status see forceUpdateIfMounted()
  componentDidMount() {
    this.componentIsMounted = true;
  }

  shouldComponentUpdate(newProps) {
    return !isEqual(
      getVisualPropsTree(newProps),
      getVisualPropsTree(this.props)
    );
  }

  // Need to track mount status see forceUpdateIfMounted()
  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  ensureUpdatedHrefForContextMenu() {
    // tracking mount status avoids errors when
    // forceUpdate() is run on unmounted component
    // which happens while components animate in.
    if (this.componentIsMounted) {
      this.forceUpdate();
    }
  }

  handleClick(event) {
    const {
      location,
      analytics,
      onClick,
      preNavigationHooks,
      target: givenTarget,
      dispatchNavigate,
      allowPropagation
    } = this.props;

    event.preventDefault();

    if (!allowPropagation) {
      event.stopPropagation();
    }

    // Cancel navigation if trying to access context menu
    if (isRightClickPressed(event)) {
      return;
    }

    if (typeof onClick === 'function') {
      onClick(event);
    }

    if (!location) {
      return;
    }

    // if this is a new tab event, target is obviously _blank
    const target = isNewTabEvent(event)
      ? '_blank'
      : this.getTarget(givenTarget, location);

    dispatchNavigate({
      analytics,
      target,
      location,
      preNavigationHooks
    });
  }

  getTarget(target, location) {
    return target || getDefaultLinkTargetFromLocation(location);
  }

  render() {
    const {
      location,
      getQualifiedLinkParams,
      target: givenTarget,
      component: LinkComponent,
      ...restProps
    } = this.props;

    const props = omit(
      restProps,
      'allowPropagation',
      'dispatch',
      'dispatchNavigate',
      'analytics',
      'href',
      'component',
      'location',
      'onClick', // don't allow onClick override
      'preNavigationHooks',
      'rel'
    );

    return (
      <LinkComponent
        onClick={this.handleClick}
        onMouseOver={this.ensureUpdatedHrefForContextMenu}
        {...(location
          ? getQualifiedLinkParams(location)
          : { rel: restProps.rel })}
        {...props}
        target={location ? this.getTarget(givenTarget, location) : null}
      />
    );
  }
}

const mapDispatchToProps = dispatch => ({
  dispatchNavigate: args => dispatch(linkNavigate(args))
});

class NavLinkContainer extends Component {
  render() {
    return <NavLink {...this.props} />;
  }
}

export default provideEnrichedLocation(
  provideQualifiedLinkParams(
    connect(
      null,
      mapDispatchToProps
    )(NavLinkContainer)
  )
);
